import { FC, useState } from "react";
import { Box, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SellIcon from "@mui/icons-material/Sell";
import ArticleIcon from "@mui/icons-material/Article";
import { useTranslation } from "../../../util";
import { Colors } from "../../../configs/colors";
import TopRightButton from "../../../components/common/top-right-button/top-right-button";
import { useNavigate } from "react-router-dom";
import { PosLayout } from "../../../components";
import { ReprintModal } from "../../../components/pos/reprint-modal/reprint-modal";

type TileDef = {
  label: string;
  icon: JSX.Element;
  description: string;
  onClick?: () => void;
};

const ICON_SX = { fontSize: "2.6rem", color: Colors.SECONDARY };

const TILE_MIN_HEIGHT = "11rem";

const SearchTile: FC<TileDef> = ({ label, icon, description, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      minHeight: TILE_MIN_HEIGHT,
      backgroundColor: Colors.WHITE,
      borderRadius: "1.2rem",
      boxShadow: "0 4px 18px rgba(0,0,0,0.07)",
      borderTop: `3px solid ${Colors.SECONDARY}`,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.6rem",
      px: "1.5rem",
      py: "2rem",
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 12px 32px rgba(0,121,194,0.16)",
      },
      "&:active": {
        transform: "translateY(-1px)",
      },
    }}
  >
    <Box
      sx={{
        width: "4rem",
        height: "4rem",
        borderRadius: "50%",
        backgroundColor: "#EEF5FB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: "1.05rem",
        fontWeight: 700,
        color: Colors.TEXT_GRAY_DARK,
        textAlign: "center",
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8rem",
        color: Colors.ACCENT_GRAY_DISABLED,
        textAlign: "center",
        lineHeight: 1.4,
      }}
    >
      {description}
    </Typography>
  </Box>
);

const ReprintLobbyPage: FC = () => {
  const translations = useTranslation();
  const navigate = useNavigate();

  const [openReprint, setOpenReprint] = useState(false);
  const [reprintTab, setReprintTab] = useState<"invoice" | "pintag" | "bagtag" | "manifest">("invoice");
  const [invoiceNo, setInvoiceNo] = useState<string>("");
  const [pinTagNo, setPinTagNo] = useState<string>("");

  const handleOpenReprint = (tab: "invoice" | "pintag" | "bagtag" | "manifest", preset?: string) => {
    setReprintTab(tab);
    if (tab === "invoice") setInvoiceNo(preset ?? "");
    else setPinTagNo(preset ?? "");
    setOpenReprint(true);
  };

  const tiles: TileDef[] = [
    {
      label: "Reprint Tag",
      icon: <DescriptionIcon sx={ICON_SX} />,
      description: "Reprint a tag by pintag number",
      onClick: () => handleOpenReprint("pintag"),
    },
    {
      label: "Reprint Invoice",
      icon: <ReceiptLongIcon sx={ICON_SX} />,
      description: "Reprint an invoice by invoice number",
      onClick: () => handleOpenReprint("invoice"),
    },
    {
      label: "Reprint Bag Tag",
      icon: <SellIcon sx={ICON_SX} />,
      description: "Reprint a bag tag by bag number",
      onClick: () => handleOpenReprint("bagtag"),
    },
    {
      label: "Reprint Manifest",
      icon: <ArticleIcon sx={ICON_SX} />,
      description: "Reprint a manifest by manifest number",
      onClick: () => handleOpenReprint("manifest"),
    },
  ];

  const PLACEHOLDER_COUNT = 8;

  return (
    <PosLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          px: "2.5rem",
          pt: "1.5rem",
          pb: "1rem",
          gap: "1.5rem",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                color: Colors.SECONDARY,
                lineHeight: 1.3,
              }}
            >
              {translations.companyName}
            </Typography>
            <Typography
              sx={{
                fontSize: "2rem",
                fontWeight: 700,
                color: Colors.TEXT_GRAY_DARK,
                lineHeight: "2.4rem",
                letterSpacing: "-0.02em",
              }}
            >
              Reprint Lobby
            </Typography>
          </Box>
          <TopRightButton variation="Back" onClick={() => navigate("/pos/lobby")} />
        </Box>

        {/* ── Tile grid ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: Colors.BACKGROUND_GRAY,
            borderRadius: "1.2rem",
            p: "1.75rem",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: Colors.PRIMARY,
              mb: "1.25rem",
            }}
          >
            Select a Reprint Type
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "1.25rem",
            }}
          >
            {tiles.map((tile) => (
              <SearchTile key={tile.label} {...tile} />
            ))}

            {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
              <Box
                key={`placeholder-${i}`}
                sx={{
                  minHeight: TILE_MIN_HEIGHT,
                  borderRadius: "1.2rem",
                  border: `1.5px dashed ${Colors.ACCENT_GRAY}`,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                  opacity: 0.45,
                }}
              />
            ))}
          </Box>
        </Box>

        <ReprintModal
          open={openReprint}
          onClose={() => setOpenReprint(false)}
          selectedTab={reprintTab}
          invoiceNo={invoiceNo}
          pinTagNo={pinTagNo}
        />
      </Box>
    </PosLayout>
  );
};

export default ReprintLobbyPage;
